import { FC, MouseEvent, ReactElement, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	AwardRibbonOutline,
	Box,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	CogOutline,
	DotsHorizontalOutline,
	HistoryOutline,
	HomeOutline,
	HStack,
	LeftDrawerCollapseOutline,
	LeftDrawerExpandOutline,
	rem,
	ShoppingCartOutline,
	Stack,
	TargetOutline,
	Text,
	Tooltip,
	VStack,
} from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { Permission, ProtectedContent, useCurrentCompany, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { Reveal } from '../../../components/display/reveal';
import { SeasonalToggle } from '../../../components/seasonal/seasonal-toggle';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { AppPath } from '../../admin-navbar/config/router';
import { useCurrentUserReports } from '../../team-management/hooks/use-current-user-reports';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useAdminNavLink } from '../hooks/use-admin-nav-link';
import { CompanyLogo } from './company-logo';
import { CreateNewMenu } from './create-new-menu';
import { IconAndTextContent } from './icon-and-text-content';
import { NotificationPanel } from './notification';
import { SearchNavItem } from './search-nav-item';
import { TeamMenu } from './team-menu';
import { TeamPanel } from './team-panel';
import { TrialCard } from './trial-card';
import { UserMenu } from './user-menu';
import { UserNavLink } from './user-nav-link';
import { UserNavMenu } from './user-nav-menu';

interface UserNavbarProps {
	isExpanded: boolean;
	toggleSidebar: () => void;
}

export const UserNavbar: FC<UserNavbarProps> = ({ isExpanded, toggleSidebar }) => {
	const { t } = useTranslation('navigation');
	const { t: c } = useTranslation('common');
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const user = useCurrentUser();
	const isAdmin = user.global_admin;
	const canViewAdmin = usePermission(Permission.CanViewAdmin);
	const { hide_competitive_features: hideCompetitiveFeatures } = useCurrentCompany();
	const showLeaderboard = !(hideCompetitiveFeatures && !isAdmin);

	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const adminNavLink = useAdminNavLink();
	const { unreadCount } = useFetchNotifications(user.locale);
	const { data: directReports = [] } = useCurrentUserReports();

	// Panel states
	const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
	const [isTeamPanelOpen, setIsTeamPanelOpen] = useState(false);

	// Scrollable nav overflow detection
	const scrollableNavRef = useRef<HTMLDivElement>(null);
	const hasOverflow = useHasOverflow(scrollableNavRef);

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	const handleOpenNotifications = () => {
		setIsTeamPanelOpen(false);
		setIsNotificationPanelOpen(true);
	};

	const handleCloseNotifications = () => {
		setIsNotificationPanelOpen(false);
	};

	const handleToggleTeamPanel = () => {
		setIsNotificationPanelOpen(false);
		setIsTeamPanelOpen((prev) => !prev);
	};

	const handleCloseTeamPanel = () => {
		setIsTeamPanelOpen(false);
	};

	return (
		<VStack bgColor="bg.canvas" w="full" h="100%" px={rem(8)} py={rem(20)} spacing={0} align="flex-start">
			<HStack justifyContent="center" height={rem(36)} width="100%" px={rem(10)} mb={rem(24)}>
				{isExpanded ? (
					<HStack justifyContent="space-between" alignItems="center" width="100%">
						<Box as={Link} id="primary-nav-logo" h={rem(36)} to={homeURL} onClick={handleLogoClick}>
							<CompanyLogo />
						</Box>
						<Tooltip label={t('collapseSidebar')} placement="right">
							<Box as="span" display="inline-flex">
								<LeftDrawerCollapseOutline
									data-testid="collapse-sidebar"
									cursor="pointer"
									onClick={toggleSidebar}
									size="md"
									color="text.softest"
									paddingInline={0}
								/>
							</Box>
						</Tooltip>
					</HStack>
				) : (
					<Tooltip label={t('expandSidebar')} placement="right">
						<Box as="span" display="inline-flex">
							<LeftDrawerExpandOutline data-testid="expand-sidebar" cursor="pointer" onClick={toggleSidebar} size="md" color="text.softest" paddingInline={0} />
						</Box>
					</Tooltip>
				)}
			</HStack>

			<Stack as="nav" spacing={1} flexDir="column" width="100%" flex={1} minHeight={0}>
				{/* Scrollable nav items */}
				<Box
					ref={scrollableNavRef}
					flex={1}
					overflowY="auto"
					minHeight={0}
					borderBottom={hasOverflow ? '1px solid' : 'none'}
					borderColor="border.default"
					mx={rem(-8)}
					px={rem(8)}
					pb={hasOverflow ? rem(8) : 0}
				>
					<VStack spacing={1} align="stretch">
						{/* HOME */}
						<UserNavLink to="/" isExpanded={isExpanded} data-testid="nav-link-home">
							<IconAndTextContent icon={<HomeOutline size="md" />} label={t('links.home')} isExpanded={isExpanded} />
						</UserNavLink>

						{/* SEARCH */}
						<SearchNavItem isExpanded={isExpanded} />

						{/* 1:1s */}
						<UserNavLink to="/growth/meet" isExpanded={isExpanded} data-testid="nav-link-one-on-ones">
							<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded={isExpanded} />
						</UserNavLink>

						{/* GOALS */}
						<UserNavLink to="/growth/goals" isExpanded={isExpanded} data-testid="nav-link-goals">
							<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded={isExpanded} />
						</UserNavLink>

						{/* RECAPS */}
						<UserNavLink to="/growth/recap-review" isExpanded={isExpanded} data-testid="nav-link-recaps">
							<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded={isExpanded} />
						</UserNavLink>

						{/* REWARDS */}
						<UserNavLink to="/rewards" isExpanded={isExpanded} data-testid="nav-link-rewards">
							<IconAndChildContent icon={<ShoppingCartOutline size="md" />} isExpanded={isExpanded}>
								<HStack justifyContent="space-between" alignItems="flex-start" width="100%">
									<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
										{t('links.rewards')}
									</Text>
									<Box bgColor="points.bgAlpha" fontWeight="semibold" fontSize={1} color="points.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
										+{c('intl_number', { value: user.earning_balance })}
									</Box>
								</HStack>
							</IconAndChildContent>
						</UserNavLink>

						{/* MORE MENU ITEMS */}
						<UserNavMenu buttonIcon={<DotsHorizontalOutline size="md" />} buttonText={t('moreButtonText')} isExpanded={isExpanded}>
							{/* INCENTIVES */}
							<UserNavLink to="/incentives" isExpanded data-testid="nav-link-incentives">
								<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded />
							</UserNavLink>
							{/* LEADERBOARD */}
							{/* NOTE: don't use Reveal/Conceal here because the Reveal/Conceal component returns a ReactElement which will cause the UserNavMenu to render an extra empty div */}
							{showLeaderboard && (
								<UserNavLink to="/analytics/leaderboards" isRecognizeRoute={true} isExpanded data-testid="nav-link-leaderboard">
									<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
								</UserNavLink>
							)}
							{/* FEEDBACK */}
							<UserNavLink to="/growth/feedback" isExpanded data-testid="nav-link-feedback">
								<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded />
							</UserNavLink>
						</UserNavMenu>

						{/* TEAM NAV ITEMS */}
						<ProtectedContent permissions={Permission.CanAccessTeamHub}>
							<TeamMenu isExpanded={isExpanded} isTeamPanelOpen={isTeamPanelOpen} onToggleTeamPanel={handleToggleTeamPanel} />
						</ProtectedContent>

						{/* CREATE NEW MENU */}
						<CreateNewMenu isExpanded={isExpanded} />
					</VStack>
				</Box>

				{/* Fixed bottom items - always visible */}
				<VStack spacing={rem(8)} flexShrink={0}>
					{/* SEASONAL THEME TOGGLE */}
					<Reveal if={isExpanded}>
						<Box w="100%" px={rem(4)}>
							<SeasonalToggle />
						</Box>
					</Reveal>

					{/* TRIAL CARD */}
					<TrialCard isExpanded={isExpanded} />

					{/* ADMIN MENU */}
					{canViewAdmin && (
						<UserNavLink to={adminNavLink.to} isRecognizeRoute={adminNavLink.isRecognizeRoute} isExpanded={isExpanded} data-testid="nav-link-admin">
							<IconAndTextContent icon={<CogOutline size="md" />} label={t('links.adminSettings')} isExpanded={isExpanded} />
						</UserNavLink>
					)}

					{/* USER MENU */}
					<UserMenu isExpanded={isExpanded} onOpenNotifications={handleOpenNotifications} unreadCount={unreadCount} />
				</VStack>
			</Stack>

			{/* Notification Panel */}
			<NotificationPanel isOpen={isNotificationPanelOpen} onClose={handleCloseNotifications} isSidebarExpanded={isExpanded} />

			{/* Team Panel */}
			<TeamPanel isOpen={isTeamPanelOpen} onClose={handleCloseTeamPanel} isSidebarExpanded={isExpanded} directReports={directReports} />
		</VStack>
	);
};

type IconAndChildContentProps = {
	icon: ReactElement;
	isExpanded: boolean;
	children: ReactNode;
};

const IconAndChildContent: FC<IconAndChildContentProps> = ({ children, icon, isExpanded }) => (
	<HStack justifyContent={isExpanded ? 'flex-start' : 'center'} alignItems={isExpanded ? 'flex-start' : 'center'}>
		{icon}
		<Reveal if={isExpanded}>{children}</Reveal>
	</HStack>
);

function useHasOverflow(ref: RefObject<HTMLDivElement>) {
	const [hasOverflow, setHasOverflow] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const checkOverflow = () => {
			setHasOverflow(element.scrollHeight > element.clientHeight);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [ref]);

	return hasOverflow;
}
